import { html, nothing, PropertyValues, TemplateResult } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { styles } from '../view/styles';
import bb from 'billboard.js/dist/billboard.pkgd';
import { WEB_COMPONENT_GZ_DONUT_CHART_WIDGET } from '../../../webcomponents.constants';
import { GzGenericWidgetContent } from '../../../genericDashboard/gzGenericWidget';
import { WidgetStateFrontEnd } from '../../../genericDashboard/gzGenericWidget/types';
import { ChartSliceClickInfo, DonutChartData, DonutChartWidgetEvents } from '../types';
import { formatLargeNumber } from '../../../utils/formatter.utils';
import { classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit/directives/style-map.js';
import { FontSizes } from '../../../types/common/types';
import { litTemplateToString } from '../../../utils/dom';
import {
  ChartStyle,
  DonutChartWidgetPayload,
  DonutChartWidgetSliceConfig,
  DonutChartWidgetSliceData,
} from '@nirvana/shared/dtos/types/widgets/common';

/**
 * @summary This is a Web Component representing a general donut chart with a legend. It is based on the DonutChart
 * implementation of the billboard.js library, and it's made to be used as a widget in the nirvana dashboard framework.
 * https://naver.github.io/billboard.js/demo/#Chart.DonutChart
 * @tag gz-donut-chart-widget
 */
@customElement(WEB_COMPONENT_GZ_DONUT_CHART_WIDGET)
export class GzDonutChartWidgetWebComponent extends GzGenericWidgetContent {
  @property({ type: WidgetStateFrontEnd, attribute: true, reflect: true })
  state: WidgetStateFrontEnd;

  @property({ type: Object, attribute: true })
  payload: DonutChartWidgetPayload;

  @property({ type: Array, attribute: true, reflect: true })
  resizeBreakingPoints: number[] = [];

  @property({ type: Boolean, attribute: true, reflect: true })
  horizontalTooltip = false;

  @property({ type: String, attribute: true, reflect: true })
  chartStyle = ChartStyle.CLASSIC;

  @query('#chart-container')
  chartContainer: HTMLElement;

  @state()
  private chartData: DonutChartData;

  private defaultEmptyColor = '#e0e0e0';
  private defaultLineWidth = 10; // pixels
  private defaultChartSize = 160; // pixels
  private defaultChartSmallSize = 130; // pixels

  static get styles() {
    return styles;
  }

  willUpdate(properties: PropertyValues) {
    super.willUpdate(properties);

    // payload was just updated, we may access it to check some options
    if (properties.has('payload')) {
      this.horizontalTooltip = this.payload.donutChart?.config.tooltipType === 'horizontal';
      this.chartStyle = this.payload.donutChart?.config.chartStyle ?? ChartStyle.CLASSIC;
    }
  }

  async updated(changedProperties: PropertyValues): Promise<void> {
    await this.updateComplete;
    if (changedProperties.has('chartData') && changedProperties.get('chartData') !== undefined) {
      return;
    }
    this.buildChart();
  }

  protected handleResize(): void {
    super.handleResize();
    this.buildChart(false);
  }

  protected renderContent(): TemplateResult {
    const mainContainerClasses = {
      'main-container': true,
      'small-size': this.isSmallWidgetSize(),
    };
    const mainContainerStyle = { '--chart-size': `${this.getChartSize()}px` };
    const chartWrapperClasses = {
      'chart-wrapper': true,
      hidden: this.isSmallWidgetSize() && this.shouldRenderInfoBox(),
    };

    return html`
      <div class="${classMap(mainContainerClasses)}" style="${styleMap(mainContainerStyle)}">
        <div class="section-content">
          <div class="${classMap(chartWrapperClasses)}">
            <div id="chart-container"></div>
            ${this.renderChartCentralInfo()}
          </div>
          ${this.renderLegendContainer()}
        </div>
      </div>
    `;
  }

  protected renderInfoBox(): TemplateResult | undefined {
    const infoBoxText = this.payload?.donutChart?.config?.infoBoxText;
    if (!infoBoxText) {
      return;
    }
    if (this.state === WidgetStateFrontEnd.IS_INFO) {
      return this.renderNoLicenseInfoBox(infoBoxText);
    }
    if (this.state === WidgetStateFrontEnd.IS_CHANGED) {
      return this.renderChangedInfoBox(infoBoxText);
    }
  }

  protected renderLoader(): TemplateResult {
    return html`
      <div class="${this.isSmallWidgetSize() ? `loading-container-small-size` : 'loading-container'}">
        <gz-loader class="flex-3"></gz-loader>
        <gz-loader class="flex-5"></gz-loader>
      </div>
    `;
  }

  private renderChartCentralInfo(): TemplateResult | undefined {
    if (!this.chartData) {
      return;
    }

    const classes = this.payload?.donutChart?.config?.labelTextMultiline
      ? 'chart-central-info-text-multiline'
      : 'chart-central-info';

    return html` <div class="${classes}">
      ${this.renderChartCentralValue(this.chartData.value)} ${this.renderChartCentralLabel(this.chartData.label)}
    </div>`;
  }

  private renderChartCentralValue(value: string): TemplateResult {
    const fontSize = this.hasSmallFontSizes() ? FontSizes.BODY : FontSizes.HEADING_3;
    const displayValue = Number.isNaN(Number(value)) ? value : formatLargeNumber(value);
    const element = html` <gz-text fontSize="${fontSize}">${displayValue}</gz-text>`;
    return this.renderWithTooltip(value, element, this.shouldShowTooltips() && Number(value) > 1000);
  }

  private renderChartCentralLabel(label: string): TemplateResult {
    const fontSize = this.hasSmallFontSizes() ? FontSizes.PARAGRAPH_12 : FontSizes.HEADING_6;
    const element = html` <gz-text fontSize="${fontSize}">${label}</gz-text>`;
    return this.renderWithTooltip(label, element, true, true, 'central-label');
  }

  private renderLegendItem(sliceData: DonutChartWidgetSliceData): TemplateResult | undefined {
    const sliceConfig = this.getSliceConfig(sliceData.id);
    if (!sliceConfig) {
      return;
    }
    const bulletStyle = {
      backgroundColor: this.state === WidgetStateFrontEnd.IS_ACTIVE ? sliceConfig.color : this.getEmptyStateColor(),
    };
    return html` <div
      class="chart-legend-item"
      @click="${() => this.handleChartLegendItemClick(sliceConfig, sliceData)}"
    >
      <span class="chart-legend-bullet" style="${styleMap(bulletStyle)}"></span>
      <div class="chart-legend ${this.state === WidgetStateFrontEnd.IS_ACTIVE ? 'clickable' : nothing} fs-14">
        ${this.renderLegendItemName(sliceConfig.label)} ${this.renderLegendItemValue(sliceData.value)}
      </div>
    </div>`;
  }

  /**
   * Handles the click event on the legend item.
   * @param {DonutChartWidgetSliceConfig} sliceConfig - The configuration of the clicked slice.
   * @param {DonutChartWidgetSliceData} sliceData - The data of the clicked slice.
   */
  protected handleChartLegendItemClick(
    sliceConfig: DonutChartWidgetSliceConfig,
    sliceData: DonutChartWidgetSliceData
  ): void {}

  private renderLegendItemName(itemName: string): TemplateResult {
    const displayName = this.state !== WidgetStateFrontEnd.IS_ERROR ? itemName : '...';
    const element = html`<span class="chart-legend-text">${displayName}</span>`;
    return this.renderWithTooltip(itemName, element, true, true);
  }

  private renderLegendItemValue(itemValue: number): TemplateResult {
    const displayValue = this.state === WidgetStateFrontEnd.IS_ACTIVE ? formatLargeNumber(itemValue) : '_';
    const element = html`<span class="chart-legend-value">${displayValue}</span>`;
    return this.renderWithTooltip(itemValue, element, this.shouldShowTooltips() && itemValue > 1000);
  }

  private renderWithTooltip(
    tooltipValue: number | string,
    element: TemplateResult,
    condition: boolean,
    isEllipsis = false,
    classes = ''
  ): TemplateResult {
    if (condition) {
      return html`
        <gz-tooltip tooltipText="${tooltipValue}" ?isEllipsis="${isEllipsis}" class="${classes}">
          ${element}
        </gz-tooltip>
      `;
    }
    return element;
  }

  protected renderLegendContainer(): TemplateResult | undefined {
    if (this.payload?.donutChart?.config?.hideLegend) {
      return;
    }
    return html` <div class="chart-legend-container">${this.renderInfoBox() ?? this.renderLegend()}</div>`;
  }

  private renderLegend(): TemplateResult {
    return html` <div class="chart-legend">
      ${this.payload?.donutChart?.data?.slices.map(item => this.renderLegendItem(item))}
    </div>`;
  }

  /**
   * Renders a tooltip when hovering slices in the BB donut chart. The elements are laid out vertically.
   * The tooltip includes the numeric value of the slice and its percentage of the total donut.
   */
  private renderBBChartTooltipVertical([{ value, ratio }]): string {
    // tooltip used for the donut chart made with billboard.js library
    return `
      <div class="chart-tooltip">
        <span>${value}</span>
        <span>${Math.round(ratio * 100)}%</span>
      </div>
    `;
  }

  /**
   * Renders a tooltip when hovering slices in the BB donut chart. The elements are laid out horizontally.
   * The tooltip includes the color of the slice, the label and the numeric value.
   *
   * NOTE: the `id` field provided by the BB library on this callback is actually the label
   */
  private renderBBChartTooltipHorizontal([{ id: label, value, ratio }]): string {
    const sliceConfig = this.getSliceConfigByLabel(label);
    const bulletStyle = {
      backgroundColor: this.state === WidgetStateFrontEnd.IS_ACTIVE ? sliceConfig?.color : this.getEmptyStateColor(),
    };

    // BB charts do not support rendering Lit components, we need to return plain HTML
    return litTemplateToString(html`
      <div class="chart-tooltip">
        <span class="chart-legend-bullet" style="${styleMap(bulletStyle)}"></span>
        <div class="chart-legend fs-14">
          ${this.renderLegendItemName(sliceConfig?.label ?? 'placeholder')} ${this.renderLegendItemValue(value)}
        </div>
      </div>
    `);
  }

  private isSmallWidgetSize(): boolean {
    return !this.hasResizeLimits() && this.currentWidthInterval[1] === this.resizeBreakingPoints[0];
  }

  private hasSmallFontSizes(): boolean {
    // when the widget size is small, always decrease the font size of the chart label & value if the chartSize
    // except when chartSize is set to the same value as chartSmallSize
    if (this.isSmallWidgetSize()) {
      const chartSmallSize = this.payload?.donutChart?.config?.chartSmallSize ?? this.defaultChartSmallSize;
      const chartSize = this.payload?.donutChart?.config?.chartSize ?? this.defaultChartSize;
      return chartSmallSize !== chartSize;
    }
    return false;
  }

  private getSliceConfig(sliceId: string): DonutChartWidgetSliceConfig | undefined {
    return this.payload?.donutChart?.config?.slices.find(slice => slice.id === sliceId);
  }

  private getSliceConfigByLabel(sliceLabel: string): DonutChartWidgetSliceConfig | undefined {
    return this.payload?.donutChart?.config?.slices.find(slice => slice.label === sliceLabel);
  }

  private getEmptyStateColor(): string {
    return this.payload?.donutChart?.config?.emptyStateColor ?? this.defaultEmptyColor;
  }

  private getLineWidth(): number {
    // in pixels
    return this.payload?.donutChart?.config?.lineWidth ?? this.defaultLineWidth;
  }

  private getChartSize(): number {
    // in pixels
    if (this.isSmallWidgetSize()) {
      return this.payload?.donutChart?.config?.chartSmallSize ?? this.defaultChartSmallSize;
    }
    return this.payload?.donutChart?.config?.chartSize ?? this.defaultChartSize;
  }

  private shouldRenderInfoBox(): boolean {
    return (
      !!this.payload?.donutChart?.config?.infoBoxText &&
      (this.state === WidgetStateFrontEnd.IS_INFO || this.state === WidgetStateFrontEnd.IS_CHANGED)
    );
  }

  private shouldShowTooltips(): boolean {
    return this.payload?.donutChart?.config?.showTooltips ?? true;
  }

  private onSliceClick(info: ChartSliceClickInfo): void {
    this.dispatchEvent(
      new CustomEvent(DonutChartWidgetEvents.SLICE_CLICK, {
        detail: { info },
        bubbles: true,
        composed: true,
      })
    );
  }

  private computeChartData(): void {
    let value = 0;
    let columns: (string | number)[][] = [];
    let colors = {};
    const label = this.payload?.donutChart?.config?.label ?? '';

    if (this.state !== WidgetStateFrontEnd.IS_ACTIVE) {
      columns = [['empty', 100]];
      colors = { ['empty']: this.getEmptyStateColor() };
      this.chartData = { value: '_ _', label, columns, colors };
      return;
    }

    this.payload.donutChart?.data.slices.forEach(slice => {
      const sliceConfig = this.getSliceConfig(slice.id);
      if (!sliceConfig || slice.value === 0) {
        return;
      }
      columns.push([sliceConfig.label, slice.value]);
      value += slice.value;
      colors[sliceConfig.label] = sliceConfig.color;
    });
    this.chartData = { value: value.toString(), label, columns, colors };
  }

  private buildChart(computeChartData = true): void {
    if (!this.payload || !this.chartContainer) {
      return;
    }
    this.chartContainer.innerHTML = '';
    if (!this.chartData || computeChartData) {
      this.computeChartData();
    }
    this.generateChart();
  }

  private generateChart(): any {
    if (!this.chartData || !this.chartContainer) {
      return;
    }
    const tooltipType = this.payload.donutChart?.config.tooltipType ?? 'vertical';
    const chartContainer = this.chartContainer;
    const chartStyle = this.chartStyle;

    return bb.generate({
      data: {
        columns: this.chartData.columns,
        onclick: this.onSliceClick.bind(this),
        type: 'donut',
        colors: this.chartData.colors,
      },
      donut: {
        label: {
          show: false,
        },
        width: this.getLineWidth(),
        padAngle: 0.05,
      },
      expand: false,
      tooltip: {
        show: this.shouldShowTooltips(),
        contents:
          tooltipType === 'vertical'
            ? this.renderBBChartTooltipVertical.bind(this)
            : this.renderBBChartTooltipHorizontal.bind(this),
      },
      legend: {
        show: false,
      },
      interaction: {
        enabled: !this.payload?.donutChart?.config?.disableSliceInteraction,
      },
      bindto: this.chartContainer,
    });
  }
}
